/**
 * LoginStep — Social Auth screen
 *
 * Step 1: Google, Apple, Meta sign-in + email fallback.
 * In mock mode, clicking any option simulates a successful login.
 */

import { useState } from 'preact/hooks'

interface LoginStepProps {
  onLogin: (method: 'email' | 'google' | 'apple' | 'meta', email?: string) => void
  clubName: string
  brandImageUrl?: string
  brandImageDarkUrl?: string
}

export function LoginStep({ onLogin, clubName, brandImageUrl, brandImageDarkUrl }: LoginStepProps) {
  const [email, setEmail] = useState('')
  const [showEmail, setShowEmail] = useState(false)
  const [error, setError] = useState('')

  const handleEmailSubmit = () => {
    if (!email.trim()) {
      setError('Please enter your email')
      return
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email')
      return
    }
    setError('')
    onLogin('email', email)
  }

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter') handleEmailSubmit()
  }

  return (
    <div className="bw2-step-login" role="form" aria-label="Sign in to book a tee time">
      {/* Brand Image — show the main logo (emblem) */}
      {brandImageUrl && (
        <div className="bw2-login__brand-image">
          <img
            src={brandImageUrl}
            alt={clubName}
            style={{ display: 'block' }}
          />
        </div>
      )}

      {/* Header */}
      <div className="bw2-login__header">
        <h2 className="bw2-login__title" id="bw2-login-heading">Request a Tee Time</h2>
        <div className="bw2-login__subtitle">
          Sign in to view available tee times at {clubName}
        </div>
      </div>

      {/* Social Auth Buttons */}
      <div className="bw2-login__social-buttons">
        {/* Google */}
        <button
          className="bw2-login__social-btn"
          onClick={() => onLogin('google')}
          type="button"
          aria-label="Continue with Google"
        >
          <svg className="bw2-login__social-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </button>

        {/* Apple */}
        <button
          className="bw2-login__social-btn bw2-login__social-btn--apple"
          onClick={() => onLogin('apple')}
          type="button"
          aria-label="Continue with Apple"
        >
          <svg className="bw2-login__social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          Continue with Apple
        </button>

        {/* Meta / Facebook */}
        <button
          className="bw2-login__social-btn bw2-login__social-btn--meta"
          onClick={() => onLogin('meta')}
          type="button"
          aria-label="Continue with Facebook"
        >
          <svg className="bw2-login__social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Continue with Facebook
        </button>
      </div>

      {/* Divider */}
      <div className="bw2-login__divider" aria-hidden="true">
        <span className="bw2-login__divider-line" />
        <span className="bw2-login__divider-text">or</span>
        <span className="bw2-login__divider-line" />
      </div>

      {/* Email toggle / input */}
      {!showEmail ? (
        <button
          className="bw2-login__social-btn"
          onClick={() => setShowEmail(true)}
          type="button"
          style={{ marginTop: '4px' }}
        >
          <svg className="bw2-login__social-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="M22 4L12 13 2 4" />
          </svg>
          Continue with Email
        </button>
      ) : (
        <>
          <div className="bw2-login__email-group">
            <label className="bw2-login__label" htmlFor="bw2-login-email">Email Address</label>
            <input
              id="bw2-login-email"
              type="email"
              className="bw2-login__input"
              placeholder="your@email.com"
              value={email}
              onInput={(e) => { setEmail((e.target as HTMLInputElement).value); setError('') }}
              onKeyDown={handleKeyPress}
              aria-invalid={!!error}
              aria-describedby={error ? 'bw2-login-email-error' : undefined}
              autoComplete="email"
              autoFocus
            />
          </div>

          {error && (
            <div className="bw2-login__error" id="bw2-login-email-error" role="alert">
              {error}
            </div>
          )}

          <button
            className="bw2-login__submit-btn"
            onClick={handleEmailSubmit}
            disabled={!email.trim()}
            type="button"
          >
            Continue →
          </button>
        </>
      )}

      {/* Terms */}
      <div className="bw2-login__terms">
        By continuing, you agree to our{' '}
        <a href="https://currentsgolf.com/terms-of-service" target="_blank" rel="noopener noreferrer">Terms</a>
        {' '}and{' '}
        <a href="https://currentsgolf.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      </div>
    </div>
  )
}
